import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import type {
  InventoryLead,
  InventoryLeadAttachment,
  InventoryLeadNegotiationEntry,
  InventoryLeadNote,
  InventoryLeadStatusHistoryEntry,
  InventoryLeadTruck,
} from "../leads/inventoryLead";
import type { AppliedEstimatedRepairAddon } from "../expenses/expenseSettings";

const convertTimestamp = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "object" && value !== null && "seconds" in value) {
    const seconds = (value as { seconds: unknown }).seconds;
    if (typeof seconds === "number") return new Date(seconds * 1000);
  }
  return null;
};

const serializeDate = (value: Date | undefined | null): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return new Date().toISOString();
};

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

/** Rep ids are portal user ids; legacy docs may hold a numeric sales-rep id. */
const parseAssignedRepId = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
};

const parsePhotoPaths = (raw: unknown): string[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const paths = raw
    .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
    .map((p) => p.trim());
  return paths.length > 0 ? paths : undefined;
};

const parseRepairAddons = (raw: unknown): AppliedEstimatedRepairAddon[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const out: AppliedEstimatedRepairAddon[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === "string" ? e.id : "";
    const amount =
      typeof e.amount === "number" && Number.isFinite(e.amount) ? e.amount : NaN;
    const category =
      e.category === "body" || e.category === "mechanical" || e.category === "safety"
        ? e.category
        : null;
    const quantityRaw =
      typeof e.quantity === "number" && Number.isFinite(e.quantity) ? e.quantity : 1;
    const quantity = Math.min(20, Math.max(1, Math.floor(quantityRaw)));
    if (!id || !category || !Number.isFinite(amount)) continue;
    out.push({ id, amount, category, quantity });
  }
  return out.length > 0 ? out : undefined;
};

const serializeRepairAddons = (addons: AppliedEstimatedRepairAddon[] | undefined) => {
  if (!Array.isArray(addons)) return [];
  return addons
    .map((addon) => {
      if (!addon?.id) return null;
      const amount =
        typeof addon.amount === "number" && Number.isFinite(addon.amount)
          ? addon.amount
          : 0;
      const category =
        addon.category === "body" ||
        addon.category === "mechanical" ||
        addon.category === "safety"
          ? addon.category
          : null;
      if (!category) return null;
      const quantityRaw =
        typeof addon.quantity === "number" && Number.isFinite(addon.quantity)
          ? addon.quantity
          : 1;
      const quantity = Math.min(20, Math.max(1, Math.floor(quantityRaw)));
      return { id: addon.id, amount, category, quantity };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

const serializeTruck = (t: InventoryLeadTruck) => ({
  make: t.make,
  model: t.model,
  year: t.year,
  size: t.size,
  type: t.type,
  fuel: t.fuel,
  ...(t.vin ? { vin: t.vin } : {}),
  ...(typeof t.mileage === "number" && Number.isFinite(t.mileage)
    ? { mileage: t.mileage }
    : {}),
  ...(typeof t.askingPrice === "number" && Number.isFinite(t.askingPrice)
    ? { askingPrice: t.askingPrice }
    : {}),
  ...(t.color?.trim() ? { color: t.color.trim() } : {}),
  ...(t.hasLien === true || t.hasLien === false ? { hasLien: t.hasLien } : {}),
  ...(t.hasLien === true
    ? {
        ...(t.lienLender?.trim() ? { lienLender: t.lienLender.trim() } : {}),
        ...(typeof t.lienPayoffAmount === "number" &&
        Number.isFinite(t.lienPayoffAmount)
          ? { lienPayoffAmount: t.lienPayoffAmount }
          : {}),
        ...(t.lienGoodThroughDate?.trim()
          ? { lienGoodThroughDate: t.lienGoodThroughDate.trim() }
          : {}),
        ...(t.lienAccountNumber?.trim()
          ? { lienAccountNumber: t.lienAccountNumber.trim() }
          : {}),
      }
    : {}),
  ...(t.photoStoragePaths?.length
    ? { photoStoragePaths: t.photoStoragePaths }
    : {}),
  pickupLocationId: t.pickupLocationId?.trim() ? t.pickupLocationId.trim() : null,
  repairShopLocationId: t.repairShopLocationId?.trim()
    ? t.repairShopLocationId.trim()
    : null,
  ...(typeof t.shippingMiles === "number" && Number.isFinite(t.shippingMiles)
    ? { shippingMiles: t.shippingMiles }
    : {}),
  ...(typeof t.shippingCost === "number" && Number.isFinite(t.shippingCost)
    ? { shippingCost: t.shippingCost }
    : {}),
  ...(typeof t.estimated_body === "number" && Number.isFinite(t.estimated_body)
    ? { estimated_body: t.estimated_body }
    : {}),
  ...(typeof t.estimated_mechanical === "number" &&
  Number.isFinite(t.estimated_mechanical)
    ? { estimated_mechanical: t.estimated_mechanical }
    : {}),
  ...(typeof t.estimated_safety === "number" && Number.isFinite(t.estimated_safety)
    ? { estimated_safety: t.estimated_safety }
    : {}),
  ...(typeof t.estimated_repair_cost === "number" &&
  Number.isFinite(t.estimated_repair_cost)
    ? { estimated_repair_cost: t.estimated_repair_cost }
    : {}),
  ...(t.estimated_repair_addons?.length
    ? { estimated_repair_addons: serializeRepairAddons(t.estimated_repair_addons) }
    : {}),
});

const parseTruck = (
  raw: unknown,
  legacyLeadPickupLocationId?: string | null,
): InventoryLeadTruck | null => {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  const make = typeof t.make === "string" ? t.make.trim() : "";
  const model = typeof t.model === "string" ? t.model.trim() : "";
  const size = typeof t.size === "string" ? t.size.trim() : "";
  const type = typeof t.type === "string" ? t.type.trim() : "";
  const year =
    typeof t.year === "number"
      ? t.year
      : typeof t.year === "string"
        ? Number(t.year)
        : NaN;
  const fuel = t.fuel === "Gas" || t.fuel === "Diesel" ? t.fuel : null;
  if (!make || !model || !size || !type || !fuel || !Number.isFinite(year)) {
    return null;
  }
  const vin =
    typeof t.vin === "string" && t.vin.trim() ? t.vin.trim() : undefined;
  const mileage = parseOptionalNumber(t.mileage);
  const askingPrice = parseOptionalNumber(t.askingPrice);
  const color =
    typeof t.color === "string" && t.color.trim() ? t.color.trim() : undefined;
  const photoStoragePaths = parsePhotoPaths(t.photoStoragePaths);
  const truckPickup =
    typeof t.pickupLocationId === "string" && t.pickupLocationId.trim()
      ? t.pickupLocationId.trim()
      : null;
  const pickupLocationId = truckPickup || legacyLeadPickupLocationId || null;
  const repairShopLocationId =
    typeof t.repairShopLocationId === "string" && t.repairShopLocationId.trim()
      ? t.repairShopLocationId.trim()
      : null;
  const hasLien =
    t.hasLien === true ? true : t.hasLien === false ? false : null;
  const lienLender =
    typeof t.lienLender === "string" && t.lienLender.trim()
      ? t.lienLender.trim()
      : null;
  const lienPayoffAmount = parseOptionalNumber(t.lienPayoffAmount) ?? null;
  const lienGoodThroughDate =
    typeof t.lienGoodThroughDate === "string" && t.lienGoodThroughDate.trim()
      ? t.lienGoodThroughDate.trim()
      : null;
  const lienAccountNumber =
    typeof t.lienAccountNumber === "string" && t.lienAccountNumber.trim()
      ? t.lienAccountNumber.trim()
      : null;
  const shippingMiles = parseOptionalNumber(t.shippingMiles);
  const shippingCost = parseOptionalNumber(t.shippingCost);
  const estimated_body = parseOptionalNumber(t.estimated_body);
  const estimated_mechanical = parseOptionalNumber(t.estimated_mechanical);
  const estimated_safety = parseOptionalNumber(t.estimated_safety);
  const estimated_repair_cost = parseOptionalNumber(t.estimated_repair_cost);
  const estimated_repair_addons = parseRepairAddons(t.estimated_repair_addons);
  return {
    make,
    model,
    year,
    size,
    type,
    fuel,
    ...(vin ? { vin } : {}),
    ...(mileage !== undefined ? { mileage } : {}),
    ...(askingPrice !== undefined ? { askingPrice } : {}),
    ...(color ? { color } : {}),
    ...(hasLien !== null ? { hasLien } : {}),
    ...(hasLien === true
      ? {
          ...(lienLender ? { lienLender } : {}),
          ...(lienPayoffAmount !== null ? { lienPayoffAmount } : {}),
          ...(lienGoodThroughDate ? { lienGoodThroughDate } : {}),
          ...(lienAccountNumber ? { lienAccountNumber } : {}),
        }
      : {}),
    ...(photoStoragePaths ? { photoStoragePaths } : {}),
    pickupLocationId,
    repairShopLocationId,
    ...(shippingMiles !== undefined ? { shippingMiles } : {}),
    ...(shippingCost !== undefined ? { shippingCost } : {}),
    ...(estimated_body !== undefined ? { estimated_body } : {}),
    ...(estimated_mechanical !== undefined ? { estimated_mechanical } : {}),
    ...(estimated_safety !== undefined ? { estimated_safety } : {}),
    ...(estimated_repair_cost !== undefined ? { estimated_repair_cost } : {}),
    ...(estimated_repair_addons ? { estimated_repair_addons } : {}),
  };
};

const parseStatusHistory = (raw: unknown): InventoryLeadStatusHistoryEntry[] => {
  if (!Array.isArray(raw)) return [];
  const out: InventoryLeadStatusHistoryEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === "string" ? e.id : "";
    const status = typeof e.status === "string" ? e.status : "";
    const changedBy = typeof e.changedBy === "string" ? e.changedBy : "";
    const changedAt = convertTimestamp(e.changedAt);
    if (!id || !status || !changedAt) continue;
    out.push({ id, status, changedBy, changedAt });
  }
  return out;
};

const parseNoteEntry = (entry: Record<string, unknown>): InventoryLeadNote | null => {
  const text = typeof entry.text === "string" ? entry.text.trim() : "";
  const author = typeof entry.author === "string" ? entry.author : "";
  const updatedAt =
    typeof entry.updatedAt === "string" && entry.updatedAt.trim()
      ? entry.updatedAt.trim()
      : typeof entry.createdAt === "string" && entry.createdAt.trim()
        ? entry.createdAt.trim()
        : "";
  if (!text || !author || !updatedAt) return null;
  return { text, author, updatedAt };
};

const parseNote = (data: Record<string, unknown>): InventoryLeadNote | null => {
  const rawNote = data.note;
  if (rawNote && typeof rawNote === "object" && !Array.isArray(rawNote)) {
    const parsed = parseNoteEntry(rawNote as Record<string, unknown>);
    if (parsed) return parsed;
  }

  // Legacy: `notes` was a chronological array — keep the most recent entry.
  const rawNotes = data.notes;
  if (!Array.isArray(rawNotes) || rawNotes.length === 0) return null;
  let latest: InventoryLeadNote | null = null;
  let latestTime = 0;
  for (const entry of rawNotes) {
    if (!entry || typeof entry !== "object") continue;
    const parsed = parseNoteEntry(entry as Record<string, unknown>);
    if (!parsed) continue;
    const time = new Date(parsed.updatedAt).getTime();
    if (!Number.isNaN(time) && time >= latestTime) {
      latest = parsed;
      latestTime = time;
    }
  }
  return latest;
};

const serializeNote = (note: InventoryLeadNote | null | undefined) => {
  if (!note?.text?.trim()) return null;
  return {
    text: note.text.trim(),
    author: typeof note.author === "string" ? note.author : "",
    updatedAt:
      typeof note.updatedAt === "string" && note.updatedAt.trim()
        ? note.updatedAt.trim()
        : new Date().toISOString(),
  };
};

const parseAttachments = (raw: unknown): InventoryLeadAttachment[] => {
  if (!Array.isArray(raw)) return [];
  const out: InventoryLeadAttachment[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === "string" ? e.id : "";
    const storagePath =
      typeof e.storagePath === "string" ? e.storagePath.trim() : "";
    const fileName = typeof e.fileName === "string" ? e.fileName.trim() : "";
    const fileSize =
      typeof e.fileSize === "number" && Number.isFinite(e.fileSize)
        ? e.fileSize
        : 0;
    const uploadedAt =
      typeof e.uploadedAt === "string" && e.uploadedAt.trim()
        ? e.uploadedAt.trim()
        : "";
    const uploadedBy = typeof e.uploadedBy === "string" ? e.uploadedBy : "";
    if (!id || !storagePath || !fileName || !uploadedAt || !uploadedBy) continue;
    out.push({ id, storagePath, fileName, fileSize, uploadedAt, uploadedBy });
  }
  return out;
};

const serializeAttachments = (attachments: InventoryLeadAttachment[] | undefined) => {
  if (!Array.isArray(attachments)) return [];
  return attachments
    .map((file) => {
      if (!file?.id || !file.storagePath?.trim() || !file.fileName?.trim()) {
        return null;
      }
      return {
        id: file.id,
        storagePath: file.storagePath.trim(),
        fileName: file.fileName.trim(),
        fileSize:
          typeof file.fileSize === "number" && Number.isFinite(file.fileSize)
            ? file.fileSize
            : 0,
        uploadedAt:
          typeof file.uploadedAt === "string" && file.uploadedAt.trim()
            ? file.uploadedAt.trim()
            : new Date().toISOString(),
        uploadedBy: typeof file.uploadedBy === "string" ? file.uploadedBy : "",
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

const parseNegotiationHistory = (raw: unknown): InventoryLeadNegotiationEntry[] => {
  if (!Array.isArray(raw)) return [];
  const out: InventoryLeadNegotiationEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === "string" ? e.id : "";
    const party = e.party === "seller" ? "seller" : e.party === "us" ? "us" : null;
    const createdAt =
      typeof e.createdAt === "string" && e.createdAt.trim() ? e.createdAt.trim() : "";
    const createdBy = typeof e.createdBy === "string" ? e.createdBy : "";
    if (!id || !party || !createdAt) continue;
    const truckIndex =
      typeof e.truckIndex === "number" && Number.isInteger(e.truckIndex) && e.truckIndex >= 0
        ? e.truckIndex
        : null;
    const amount = parseOptionalNumber(e.amount) ?? null;
    const note = typeof e.note === "string" && e.note.trim() ? e.note.trim() : null;
    out.push({ id, truckIndex, party, amount, note, createdAt, createdBy });
  }
  out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return out;
};

const serializeNegotiationHistory = (
  history: InventoryLeadNegotiationEntry[] | undefined,
) => {
  if (!Array.isArray(history)) return [];
  return history
    .map((entry) => {
      if (!entry?.id) return null;
      const party = entry.party === "seller" ? "seller" : "us";
      return {
        id: entry.id,
        truckIndex:
          typeof entry.truckIndex === "number" && Number.isInteger(entry.truckIndex)
            ? entry.truckIndex
            : null,
        party,
        amount:
          typeof entry.amount === "number" && Number.isFinite(entry.amount)
            ? entry.amount
            : null,
        note: entry.note?.trim() ? entry.note.trim() : null,
        createdAt:
          typeof entry.createdAt === "string" && entry.createdAt.trim()
            ? entry.createdAt.trim()
            : new Date().toISOString(),
        createdBy: typeof entry.createdBy === "string" ? entry.createdBy : "",
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

const serializeStatusHistory = (
  history: InventoryLeadStatusHistoryEntry[] | undefined,
) => {
  if (!Array.isArray(history)) return [];
  return history
    .map((entry) => {
      if (!entry?.id || !entry.status) return null;
      return {
        id: entry.id,
        status: entry.status,
        changedBy: typeof entry.changedBy === "string" ? entry.changedBy : "",
        changedAt: serializeDate(entry.changedAt),
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

export const inventoryLeadConverter: FirestoreDataConverter<InventoryLead> = {
  toFirestore(lead: InventoryLead) {
    const { id: _id, ...rest } = lead;
    return {
      leadNumber: rest.leadNumber ?? null,
      contactId: rest.contactId ?? "",
      companyId: rest.companyId ?? "",
      trucks: Array.isArray(rest.trucks) ? rest.trucks.map(serializeTruck) : [],
      status: rest.status ?? null,
      statusHistory: serializeStatusHistory(rest.statusHistory),
      assignedRepId: parseAssignedRepId(rest.assignedRepId),
      note: serializeNote(rest.note),
      attachments: serializeAttachments(rest.attachments),
      shippingPricePerMile: parseOptionalNumber(rest.shippingPricePerMile) ?? null,
      negotiationHistory: serializeNegotiationHistory(rest.negotiationHistory),
      lostReasonId:
        typeof rest.lostReasonId === "string" && rest.lostReasonId.trim()
          ? rest.lostReasonId.trim()
          : null,
      lostReasonLabel:
        typeof rest.lostReasonLabel === "string" && rest.lostReasonLabel.trim()
          ? rest.lostReasonLabel.trim()
          : null,
      lostReasonNotes:
        typeof rest.lostReasonNotes === "string" && rest.lostReasonNotes.trim()
          ? rest.lostReasonNotes.trim()
          : null,
      ...(rest.createdBy ? { createdBy: rest.createdBy } : {}),
      createdAt: serializeDate(rest.createdAt),
      updatedAt: serializeDate(rest.updatedAt),
    };
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): InventoryLead {
    const data = snapshot.data(options) ?? {};
    // Legacy leads stored a single pickupLocationId at the lead level.
    const legacyPickup =
      typeof data.pickupLocationId === "string" && data.pickupLocationId.trim()
        ? data.pickupLocationId.trim()
        : null;
    const trucks = Array.isArray(data.trucks)
      ? data.trucks
          .map((raw) => parseTruck(raw, legacyPickup))
          .filter((t): t is InventoryLeadTruck => t !== null)
      : [];

    return {
      id: snapshot.id,
      leadNumber:
        typeof data.leadNumber === "string" && data.leadNumber.trim()
          ? data.leadNumber.trim()
          : null,
      contactId: typeof data.contactId === "string" ? data.contactId : "",
      companyId: typeof data.companyId === "string" ? data.companyId : "",
      trucks,
      status:
        typeof data.status === "string" && data.status.trim()
          ? data.status.trim()
          : null,
      statusHistory: parseStatusHistory(data.statusHistory),
      assignedRepId: parseAssignedRepId(data.assignedRepId),
      note: parseNote(data),
      attachments: parseAttachments(data.attachments),
      shippingPricePerMile: parseOptionalNumber(data.shippingPricePerMile) ?? null,
      negotiationHistory: parseNegotiationHistory(data.negotiationHistory),
      lostReasonId:
        typeof data.lostReasonId === "string" && data.lostReasonId.trim()
          ? data.lostReasonId.trim()
          : null,
      lostReasonLabel:
        typeof data.lostReasonLabel === "string" && data.lostReasonLabel.trim()
          ? data.lostReasonLabel.trim()
          : null,
      lostReasonNotes:
        typeof data.lostReasonNotes === "string" && data.lostReasonNotes.trim()
          ? data.lostReasonNotes.trim()
          : null,
      ...(typeof data.createdBy === "string" && data.createdBy
        ? { createdBy: data.createdBy }
        : {}),
      createdAt: convertTimestamp(data.createdAt) || new Date(),
      updatedAt: convertTimestamp(data.updatedAt) || new Date(),
    };
  },
};
